import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/text';
import { useDeleteTableViewService } from '@/services/tableViews';
import { Trash2 } from 'lucide-react';
import { useState, type FC, type ReactNode } from 'react';
import { toast } from 'sonner';

interface DeleteTableViewModalProps {
  tableViewId: string;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const DeleteTableViewModal: FC<DeleteTableViewModalProps> = ({
  tableViewId,
  trigger,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const { deleteTableView, isDeletingTableView } = useDeleteTableViewService();

  // Use controlled open state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const handleConfirm = () => {
    deleteTableView(tableViewId, {
      onSuccess: () => {
        setIsOpen(false);
        toast.success('Table view deleted successfully');
      },
      onError: () => {
        toast.error('Failed to delete table view');
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete table view?</DialogTitle>
          <DialogDescription>
            <Text variant="body-sm" color="primary">
              Are you sure you want to delete? All data, columns, and rows will
              be permanently deleted. This action cannot be undone.
            </Text>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isDeletingTableView}
          >
            <Trash2 />
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
